import algoliasearch from 'algoliasearch/lite'
import qs from 'querystringify'
import React from 'react'
import { IconSortBy, IconSortDate, IconSortPrice, IconSortRelevance } from '../components/Icons'
import PropTypes from 'prop-types'

const appId = process.env.GATSBY_ALGOLIA_APP
const apiKey = process.env.GATSBY_ALGOLIA_KEY

export const serializeFacets = facets => {
  if (!facets) return ''
  const sanitizedFacets = {}
  Object.keys(facets).forEach(key => {
    sanitizedFacets[key] = facets[key].map(v => v.split(':')[1]).join('|')
  })
  return qs.stringify(sanitizedFacets, '')
}
export const deserializeFacets = facetsString => {
  if (!facetsString) return {}
  const facets = qs.parse(facetsString)
  if (!facets) return {}
  Object.keys(facets).forEach(key => {
    facets[key] = facets[key].split('|').map(v => `${key}:${v}`)
  })
  return facets
}

export const OptionStyle = ({ label, icon }) => {
  return (
    <i className="text-sm inline-block ml-1 text-gray-500 uppercase">
      {label} {icon}
    </i>
  )
}
OptionStyle.propTypes = {
  label: PropTypes.string,
  icon: PropTypes.node,
}

export const SortByFields = [
  {
    name: 'Relevance',
    value: 'products',
    count: 0,
    icon: (
      <div className=" w-9 h-4 translate-y-[.15rem]">
        <IconSortRelevance className="w-full h-full" />
      </div>
    ),
  },
  {
    name: 'Price - High to Low',
    value: 'products_by_price',
    count: 0,
    icon: (
      <div className="grid grid-cols-2  w-8 h-4 translate-y-[.15rem]">
        <IconSortPrice className="w-full h-full" />
        <IconSortBy className="w-full h-full scale-y-[-100%]" />
      </div>
    ),
  },
  {
    name: 'Price - Low to High',
    value: 'products_by_price_asc',
    count: 0,
    icon: (
      <div className="grid grid-cols-2  w-8 h-4 translate-y-[.15rem]">
        <IconSortPrice className="w-full h-full" />
        <IconSortBy className="w-full h-full" />
      </div>
    ),
  },
  {
    name: 'Newest to Oldest',
    value: 'products_by_date',
    count: 0,
    icon: (
      <div className="grid grid-cols-2  w-9 h-4 translate-y-[.15rem]">
        <IconSortDate className="w-full h-full" />
        <IconSortBy className="w-full h-full" />
      </div>
    ),
  },
  {
    name: 'Oldest to Newest',
    value: 'products_by_date_asc',
    count: 0,
    icon: (
      <div className="grid grid-cols-2  w-9 h-4 translate-y-[.15rem]">
        <IconSortDate className="w-full h-full" />
        <IconSortBy className="w-full h-full scale-y-[-100%]" />
      </div>
    ),
  },
]
export const extractFilter = (facets, key, remove = true) => {
  const filter = facets[key]
  if (remove) delete facets[key]
  if (!filter) return null
  return filter[0].split(':')[1]
}

const isOldFilter = (facet, filters, prevFacets) => {
  return filters && facet in filters && facet in (prevFacets || {})
}

const mergeFacet = (newFacets, facet, filters, prevFacets, searchResponse) => {
  if (isOldFilter(facet, filters, prevFacets)) {
    newFacets[facet] = prevFacets[facet]
  } else {
    newFacets[facet] = []
    for (let facetOption in searchResponse.facets[facet]) {
      if (!Object.prototype.hasOwnProperty.call(searchResponse.facets[facet], facetOption)) continue
      if ((facetOption || '').length === 0) {
        continue
      }
      newFacets[facet].push({
        name: facetOption,
        count: searchResponse.facets[facet][facetOption],
      })
    }
  }
  if ((newFacets[facet] || []).length <= 1 && !(facet in filters)) {
    delete newFacets[facet]
  }
}

export const mergeFacets = (prevFacets, filters, searchResponse) => {
  const newFacets = {}
  for (let facet in searchResponse.facets) {
    if (!Object.prototype.hasOwnProperty.call(searchResponse.facets, facet)) continue
    mergeFacet(newFacets, facet, filters, prevFacets, searchResponse)
  }
  return newFacets
}

const client = () => algoliasearch(appId, apiKey)

const query = {
  hitsPerPage: 36,
  responseFields: '*',
  attributesToRetrieve: [
    'name',
    'categories',
    'urlPath',
    'sku',
    'title',
    'small_image',
    'thumbnail',
    'item_type',
    'item_category',
    'jewelry_type',
    'brand',
    'model',
    'model_no',
    'case_size_mm',
    'price',
    'special_price',
    'qty',
    'original_box',
    'original_papers',
  ],
  attributesToHighlight: false,
  attributesToSnippet: false,
  // attributesToSnippet: '*:20',
  // snippetEllipsisText: '…',
  facets: ['*'],
}

const algoliaSearch = (
  keyword,
  page,
  filters,
  sortBy,
  _abortController,
  category = null,
  onlyAvailable = true,
  boxAndPapers = false,
  priceRange = [-1, -1],
  hitsPerPage = 36,
  skus = [],
  allFields = false
) => {
  query.hitsPerPage = hitsPerPage
  // console.log(`algoliaSearch: ${keyword}, ${page}, ${filters}, ${sortBy}, ${category}, ${onlyAvailable}, ${boxAndPapers}, ${priceRange}`)
  //
  if (Number.isNaN(page)) {
    page = 0
  }
  const ff = Object.values(filters).filter(
    kv =>
      kv &&
      kv.length > 0 &&
      !kv[0].startsWith('sort_by') &&
      !kv[0].startsWith('search') &&
      !kv[0].startsWith('min_price') &&
      !kv[0].startsWith('max_price')
  )
  if (category) {
    ff.push([`categories.id:${category.strapi_id}`])
  }
  if (skus) {
    ff.push(skus.map(s => `sku:${s}`))
  }
  if (
    onlyAvailable &&
    !ff.find(f => f && f.length > 0 && (f[0].startsWith('qty:') || f[0].startsWith('in_stock:')))
  ) {
    ff.push(['in_stock:true'])
  }
  let algoliaSortedIndex = 'products'
  const allowedSorts = [
    'products',
    'products_by_date',
    'products_by_price',
    'products_by_price_asc',
    'products_by_date_asc',
  ]
  if (sortBy) {
    const sortByField = SortByFields.find(f => f.name === sortBy || f.value === sortBy)
    if (sortByField && allowedSorts.includes(sortByField.value)) {
      algoliaSortedIndex = sortByField.value
    }
  }

  keyword = keyword.trim()
  // SKU type of search
  let additionalQueryParameters = {}
  if (keyword.indexOf(' ') === -1 && keyword.match(/^[wsnWSN][1-9][0-9]{3,5}$/)) {
    page = 0
    additionalQueryParameters = {
      synonyms: false,
      restrictSearchableAttributes: ['sku'],
      typoTolerance: false,
      hitsPerPage: 1,
    }
  }
  if (priceRange && priceRange.length === 2) {
    if (Number.isNaN(priceRange[0])) priceRange[0] = -1
    if (Number.isNaN(priceRange[1])) priceRange[1] = -1
    if (priceRange[0] !== -1 || priceRange[1] !== -1) {
      additionalQueryParameters.numericFilters = []

      if (priceRange[0] !== -1) {
        additionalQueryParameters.numericFilters.push(`final_price>=${priceRange[0]}`)
      }
      if (priceRange[1] !== -1 && priceRange[1] > priceRange[0]) {
        additionalQueryParameters.numericFilters.push(`final_price<=${priceRange[1]}`)
      }
    }
  }

  const searchProps = { ...query, page: page, facetFilters: ff, ...additionalQueryParameters }
  if (searchProps) {
    searchProps.attributesToRetrieve.push('media_gallery')
  }
  if (allFields) delete searchProps.attributesToRetrieve
  const searchClient = client()
  const index = searchClient.initIndex(algoliaSortedIndex)
  return index.search(keyword, searchProps)
}

export { algoliaSearch, client }
